import { CSSProperties, FC, ReactNode } from 'react';
import { format } from 'date-fns';
import classNames from 'classnames';
import { ColorField, ImageField } from '@prismicio/client';

import { Icon, PrismicImageWithAlt } from '@/components';
import { parsePrismicDate } from '@/utils';
import { numberToShortString } from 'utils/utils';

import style from './blog.module.scss';

export const BlogFigureImage: FC<IFigureProps> = ({
  image,
  className,
  objectFit = 'cover',
  objectPosition = 'center top',
  showPlaceholder = true,
  defaultAlt = 'Blog post image',
  noImageContent,
}) => {
  if (!image?.url && !showPlaceholder) return null;

  return (
    <figure className={classNames(style.article__figure, className)}>
      <PrismicImageWithAlt
        defaultAlt={defaultAlt}
        field={image}
        fill
        noImageContent={noImageContent}
        showPlaceholderIfEmpty={showPlaceholder}
        style={{ objectFit, objectPosition }}
      />
    </figure>
  );
};

export const BlogPublishDate: FC<IBlogPublishDateProps> = ({
  publishAt,
  className,
  label = 'Published on',
  style,
}) => {
  const date =
    typeof publishAt === 'string' ? parsePrismicDate(publishAt) : publishAt;

  if (!date) return null;

  const formattedDate = format(date, 'PPP');
  const isoDate = format(date, 'yyyy-MM-dd');

  return (
    <time
      aria-label={`${label}: ${formattedDate}`}
      className={className}
      dateTime={isoDate}
      style={style}
    >
      {formattedDate}
    </time>
  );
};

const BlogCounter: FC<IBlogCounterProps> = ({
  value = 0,
  icon = 'visualizations',
  iconColor,
  countColor,
  className,
}) => {
  if (!value) return null;

  return (
    <span
      className={className}
      style={{
        color: countColor!,
      }}
    >
      <Icon
        name={icon}
        style={{
          color: iconColor!,
        }}
      />
      {numberToShortString(value)}
    </span>
  );
};

export const BlogStatsContainer: FC<IBlogStatsProps> = ({
  reviews = 0,
  comments = 0,
  classContainer,
  countColor,
  iconColor,
}) => {
  if (!reviews && !comments) return null;

  return (
    <div className={classNames(style.stats, classContainer)}>
      <BlogCounter
        countColor={countColor}
        iconColor={iconColor}
        value={reviews!}
      />
      <BlogCounter
        countColor={countColor}
        icon="comments"
        iconColor={iconColor}
        value={comments!}
      />
    </div>
  );
};

interface IFigureProps {
  image: ImageField;
  className?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  objectPosition?: string;
  defaultAlt?: string;
  showPlaceholder?: boolean;
  noImageContent?: ReactNode;
}

interface IBlogPublishDateProps {
  publishAt?: string | Date | null;
  className?: string;
  label?: string;
  style?: CSSProperties;
}

interface IBlogCounterProps {
  value?: number;
  icon?: string;
  className?: string;
  countColor?: ColorField;
  iconColor?: ColorField;
}

interface IBlogStatsProps {
  reviews?: number;
  comments?: number;
  classContainer?: string;
  countColor?: ColorField;
  iconColor?: ColorField;
  style?: CSSProperties;
}
